import {
  MarketingShell,
  Seo,
  Container,
  Section,
  PageHero,
  SectionIntro,
  FeatureGrid,
  FeatureRow,
  StatBand,
  Steps,
  CTASection,
  Reveal,
  Mock,
} from './kit.jsx'

const INTEGRATIONS = [
  {
    icon: 'mail',
    title: 'Gmail',
    body: 'Connect your inbox with OAuth and send every sequence step as yourself. Replies come straight back to Gmail and log against the account automatically.',
    tag: 'Connected',
  },
  {
    icon: 'lock',
    title: 'Google Workspace SSO',
    body: 'Sign in with your Google Workspace account through Amazon Cognito. Managed identity, MFA support, and no extra password for your reps to remember.',
  },
  {
    icon: 'boxes',
    title: 'Verified lead data',
    body: '10,942 verified companies ship built in — companies across the verticals you sell into, cleaned and organized by industry and region.',
    tag: 'Live data',
  },
  {
    icon: 'doc',
    title: 'CSV & spreadsheet export',
    body: 'Export any filtered view — region, industry, company size, annual volume, named contacts — to CSV for your own reporting, dialers, or back-office tools.',
  },
  {
    icon: 'layers',
    title: 'DynamoDB-backed API',
    body: 'Every lead lives in per-category DynamoDB tables served by a paginated Lambda API, so regions load in under a second no matter how deep you filter.',
  },
  {
    icon: 'zap',
    title: 'Webhooks & API',
    body: 'Push new replies, stage changes, and won deals to your own endpoints, or pull lead data into internal systems with a scoped REST API.',
    tag: 'Soon',
  },
  {
    icon: 'bolt',
    title: 'Zapier',
    body: 'Wire Revailo Sales into 6,000+ apps without code — trigger on a reply or a closed-won deal and fan it out to Slack, Sheets, or your ERP.',
    tag: 'Soon',
  },
  {
    icon: 'clock',
    title: 'Calendar',
    body: 'Book inspections and site visits straight from a lead. Two-way sync with Google Calendar keeps every rep’s schedule on the account timeline.',
    tag: 'Soon',
  },
  {
    icon: 'refresh',
    title: 'CRM sync',
    body: 'Keep accounts, contacts, and pipeline stages mirrored to Salesforce or HubSpot so Revailo Sales fits the stack your office already runs on.',
    tag: 'Soon',
  },
]

const CONNECT_STEPS = [
  {
    title: 'Sign in with Google',
    body: 'Authenticate through Cognito with your Google Workspace account. Your workspace loads with all 10,942 leads already in place — no import, no setup project.',
  },
  {
    title: 'Authorize Gmail',
    body: 'Grant send-and-read access with a single OAuth consent. We never store your password — just a revocable token you control from your Google account.',
  },
  {
    title: 'Send and track',
    body: 'Launch a sequence and every email goes out from your real address. Replies route back to your inbox and post to the account timeline automatically.',
  },
]

const STATS = [
  { value: '10,942', label: 'Leads served by the API' },
  { value: 'OAuth', label: 'Gmail send & receive' },
  { value: '<1s', label: 'To load any region' },
  { value: '4', label: 'Integrations on the roadmap' },
]

export default function IntegrationsPage() {
  return (
    <MarketingShell active="integrations">
      <Seo
        title="Integrations & data"
        description="Revailo Sales connects to Gmail for outreach, Google Workspace SSO via Cognito, and a DynamoDB-backed API serving 10,942 verified B2B leads — with CSV export, webhooks, Zapier, calendar, and CRM sync on the roadmap."
        path="/integrations"
        keywords="Revailo Sales integrations, Gmail outreach, Cognito SSO, DynamoDB API, CRM sync, Zapier"
      />

      <Section>
        <PageHero
          eyebrow="Integrations"
          title={
            <>
              Plugs into the tools your <br className="max-sm:hidden" />
              office already runs on.
            </>
          }
          lead="Revailo Sales sends outreach through your own Gmail, signs your team in with Google Workspace, and serves 10,942 verified leads from a fast, API-backed data layer — with webhooks, Zapier, calendar, and CRM sync on the way."
          primary={{ label: 'Start free', to: '/signup' }}
          secondary={{ label: 'Talk to sales', to: '/contact' }}
        />
      </Section>

      <Section id="integrations">
        <Container>
          <Reveal>
            <SectionIntro
              eyebrow="What connects"
              title={<>Data in, outreach out, everything tracked.</>}
              lead="The core stack is live today — Gmail, Google SSO, and the verified data engine. More integrations are shipping through 2026."
            />
          </Reveal>
          <div className="mt-14">
            <Reveal>
              <FeatureGrid items={INTEGRATIONS} cols={3} />
            </Reveal>
          </div>
        </Container>
      </Section>

      <Section className="py-14">
        <Container>
          <Reveal>
            <StatBand stats={STATS} />
          </Reveal>
        </Container>
      </Section>

      <Section id="gmail">
        <Container>
          <FeatureRow
            eyebrow="Gmail integration"
            title="Send from your own inbox, keep every reply"
            body="Connect Gmail once with OAuth and every sequence step goes out from your real address — no shared blast domain, no deliverability hit. Replies land in your inbox and post straight to the account timeline, so the whole team sees the conversation."
            bullets={[
              'One-time OAuth consent — no password stored, revoke anytime',
              'Intro, follow-up, and break-up emails all sent as yourself',
              'Replies auto-logged against the company in Deal Flow',
              'Threading preserved so conversations stay in Gmail too',
            ]}
            media={
              <Reveal>
                <Mock kind="inbox" />
              </Reveal>
            }
          />
        </Container>
      </Section>

      <Section className="py-14" id="setup">
        <Container>
          <Reveal>
            <SectionIntro
              eyebrow="How connecting works"
              title="Two consents and you’re sending"
              lead="No IT ticket, no data migration. Sign in, authorize Gmail, and your market is loaded and ready to work."
            />
          </Reveal>
          <div className="mt-10" style={{ maxWidth: 760, margin: '40px auto 0' }}>
            <Reveal>
              <Steps items={CONNECT_STEPS} />
            </Reveal>
          </div>
        </Container>
      </Section>

      <CTASection
        eyebrow="Get started"
        title="Connect Gmail and start sending today."
        body="Sign in with Google, authorize your inbox, and work all 10,942 verified companies in your market — no credit card, no data project."
        primary={{ label: 'Start free', to: '/signup' }}
        secondary={{ label: 'Book a demo', to: '/demo' }}
      />
    </MarketingShell>
  )
}
